import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const Account = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setProfileMessage({ type: '', text: '' });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordMessage({ type: '', text: '' });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileData.displayName.trim()) {
      setProfileMessage({ type: 'error', text: 'Введите имя' });
      return;
    }

    setProfileSaving(true);
    try {
      await updateProfile(user, { displayName: profileData.displayName.trim() });
      setProfileMessage({ type: 'success', text: 'Профиль обновлен' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileMessage({ type: 'error', text: 'Ошибка обновления профиля' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword) {
      setPasswordMessage({ type: 'error', text: 'Введите текущий пароль' });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Новый пароль должен содержать минимум 6 символов' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Пароли не совпадают' });
      return;
    }

    setPasswordSaving(true);
    try {
      // Re-authenticate user before password change
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMessage({ type: 'success', text: 'Пароль изменен' });
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setPasswordMessage({ type: 'error', text: 'Неверный текущий пароль' });
      } else {
        setPasswordMessage({ type: 'error', text: 'Ошибка изменения пароля' });
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        user={{ name: user?.displayName || 'Пользователь', email: user?.email }}
        onLogout={handleLogout}
      />

      <main className="pt-16 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-text-secondary hover:text-foreground transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Назад к сайтам</span>
            </button>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
              Настройки аккаунта
            </h1>
          </div>

          {/* Profile Section */}
          <div className="bg-card rounded-2xl border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="User" size={20} />
              Профиль
            </h2>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <Input
                type="text"
                name="displayName"
                label="Имя"
                placeholder="Ваше имя"
                value={profileData.displayName}
                onChange={handleProfileChange}
                disabled={profileSaving}
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Электронная почта
                </label>
                <div className="px-4 py-3 bg-muted rounded-lg text-text-secondary">
                  {user?.email}
                </div>
                <p className="text-xs text-text-secondary mt-1">
                  Изменение email недоступно
                </p>
              </div>

              {profileMessage.text && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${
                  profileMessage.type === 'success'
                    ? 'bg-success/10 text-success'
                    : 'bg-error/10 text-error'
                }`}>
                  <Icon name={profileMessage.type === 'success' ? 'Check' : 'AlertCircle'} size={16} />
                  <span className="text-sm">{profileMessage.text}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="default"
                loading={profileSaving}
                iconName="Save"
                iconPosition="left"
              >
                Сохранить
              </Button>
            </form>
          </div>

          {/* Password Section */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Lock" size={20} />
              Смена пароля
            </h2>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  name="currentPassword"
                  label="Текущий пароль"
                  placeholder="Введите текущий пароль"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  disabled={passwordSaving}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-[38px] text-text-secondary hover:text-foreground transition-colors"
                  aria-label={showCurrentPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  <Icon name={showCurrentPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  label="Новый пароль"
                  placeholder="Минимум 6 символов"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  disabled={passwordSaving}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-[38px] text-text-secondary hover:text-foreground transition-colors"
                  aria-label={showNewPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  <Icon name={showNewPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              <Input
                type="password"
                name="confirmPassword"
                label="Подтвердите пароль"
                placeholder="Повторите новый пароль"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                disabled={passwordSaving}
              />

              {passwordMessage.text && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${
                  passwordMessage.type === 'success'
                    ? 'bg-success/10 text-success'
                    : 'bg-error/10 text-error'
                }`}>
                  <Icon name={passwordMessage.type === 'success' ? 'Check' : 'AlertCircle'} size={16} />
                  <span className="text-sm">{passwordMessage.text}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="outline"
                loading={passwordSaving}
                iconName="Key"
                iconPosition="left"
              >
                Изменить пароль
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Account;
